import { Head } from "@inertiajs/react"
import { Eye, User } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import type ProfileViewsIndex from "@/types/serializers/ProfileViewsIndex"

const ROLE_LABELS: Record<string, { label: string; className: string }> = {
  scout: {
    label: "Scout",
    className: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  },
  club: {
    label: "Club",
    className: "bg-green-500/20 text-green-400 border-green-500/30",
  },
  admin: {
    label: "Admin",
    className: "bg-red-500/20 text-red-400 border-red-500/30",
  },
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00")
  return date.toLocaleDateString("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

export default function ProfileViews({ views }: ProfileViewsIndex) {
  return (
    <AppLayout>
      <Head title="Visitas a tu perfil | KrownFT" />

      <div className="min-h-svh">
        <div className="mx-auto max-w-3xl px-4 py-6">
          <div className="mb-6 flex items-center gap-2">
            <Eye className="text-krown-300 h-5 w-5" />
            <h1 className="text-2xl font-bold">Visitas a tu perfil</h1>
          </div>

          {views.length > 0 ? (
            <div className="space-y-3">
              {views.map((view) => {
                const roleConfig =
                  ROLE_LABELS[view.viewer.role] ?? ROLE_LABELS.scout
                const organization =
                  view.viewer.scout_profile?.organization_name ??
                  view.viewer.club_profile?.name

                return (
                  <Card key={view.id}>
                    <CardContent className="flex items-center gap-4 p-4">
                      <div className="bg-krown-600 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white">
                        <User className="h-5 w-5" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold">
                          {view.viewer.full_name}
                        </h3>
                        {organization && (
                          <p className="text-muted-foreground text-sm">
                            {organization}
                          </p>
                        )}
                      </div>

                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <Badge
                          variant="outline"
                          className={roleConfig.className}
                        >
                          {roleConfig.label}
                        </Badge>
                        <span className="text-muted-foreground text-xs">
                          {formatDate(view.viewed_on)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          ) : (
            <div className="py-16 text-center">
              <Eye className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
              <p className="text-muted-foreground text-lg">
                Aún no hay visitas a tu perfil
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                Cuando scouts o clubes vean tu perfil, aparecerán aquí.
              </p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
